import {
    Link,
    Typography,
} from '@mui/material';
import {
    createStyles,
    makeStyles,
} from '@mui/styles';
import React, { useMemo } from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';

import { UiBackgroundV2 } from '../../../../common/UiBackground/UiBackgroundV2';
import UiList from '../../../../common/UiList/UiList';
import { useLocalization } from '../../../../container/Localization';
import HappyPerson from '../../../../images/HappyPerson.svg';
import { logClick } from '../../../../telemetry';
import palette from '../../../../theme/palette';
import { useRegister } from '../../../../util/hooks/Register';
import getLinks from '../util/Links';

const useStyles = makeStyles(() =>
    createStyles({
        title: {
            color: palette.light.title.primary,
            width: '100%',
            flexShrink: 0,
            fontSize: '32px',
            lineHeight: '32px',
            letterSpacing: '-0.06px',
            fontWeight: 600,
        },
        titleName: { color: palette.light.title.tertiary },
        section: {
            marginTop: '40px',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            flexShrink: 0,
        },
        listHeader: {
            color: palette.light.list.header,
            marginBottom: '15px',
            fontWeight: 700,
        },
        header: { color: palette.light.subtitle },
        link: {
            zIndex: 1,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            marginTop: '8px',
            display: 'table',
            cursor: 'pointer',
            color: palette.light.link,
            '&:hover': { textDecoration: 'underline' },
        },
        list: {
            marginTop: '24px',
            width: '100%',
            flexShrink: 0,
        },
        trademark: {
            fontWeight: 300,
            fontSize: '8px',
            position: 'relative',
            top: '-2.2em',
        },
    }),
);

const AutomationCloudForCommunity = () => {
    const classes = useStyles();
    const register = useRegister();

    const language = useLocalization();
    const links = useMemo(() => getLinks(language), [ language ]);
    const { formatMessage: translate } = useIntl();

    const items = useMemo(() =>
        [
            'COMMUNITY_SIGNUP_1',
            'COMMUNITY_SIGNUP_2',
            'COMMUNITY_SIGNUP_3',
            'COMMUNITY_SIGNUP_4',
        ], []) ;

    return (
        <UiBackgroundV2
            shapeColor={palette.light.shape}
            WelcomeImage={HappyPerson}>
            <Typography className={classes.title}>
                <FormattedMessage
                    id="AC_COMMUNITY_HEADER"
                    description="Welcome Message"
                    values={{
                        // eslint-disable-next-line react/no-unstable-nested-components
                        title: (chunks: string) => <span className={classes.titleName}>
                            {chunks}
                        </span>,
                        // eslint-disable-next-line react/no-unstable-nested-components
                        tm: (chunks: string) => <span className={classes.trademark}>
                            {chunks}
                        </span>,
                    }}
                />
            </Typography>
            <div
                className={classes.list}
                data-cy="automation-cloud-list">
                <Typography
                    variant="subtitle2"
                    className={classes.listHeader}>
                    {translate({ id: 'COMMUNITY_SMALL_TEAMS' })}
                </Typography>
                <UiList
                    items={items}
                    colorListItemCheckmark="green"
                />
            </div>
            <div className={classes.section}>
                <Typography
                    variant="subtitle1"
                    className={classes.header}>
                    {translate({ id: 'NEED_ROBOTS' })}
                </Typography>
                <Typography
                    className={classes.link}
                    onClick={() => {
                        logClick(AutomationCloudForCommunity.name, true, 'TRY_AUTOMATION_CLOUD');
                        register({ subscriptionPlan: 'trial' });
                    }}
                    data-cy="try-automation-cloud-link"

                >
                    {translate({ id: 'TRY_AUTOMATION_CLOUD' })}
                </Typography>
            </div>
            <div className={classes.section}>
                <Typography
                    variant="subtitle1"
                    className={classes.header}>
                    {translate({ id: 'PREFER_ONPREM' })}
                </Typography>
                <Typography
                    component={Link}
                    className={classes.link}
                    href={links.enterpriseServer}
                    onClick={() => {
                        logClick(AutomationCloudForCommunity.name, true, 'TRY_ENTERPRISE_SERVER');
                    }}
                    data-cy="try-enterprise-server-link"
                >
                    {translate({ id: 'TRY_ENTERPRISE_SERVER' })}
                </Typography>
                <Typography
                    component={Link}
                    className={classes.link}
                    href={links.enterpriseStudio}
                    onClick={() => {
                        logClick(AutomationCloudForCommunity.name, true, 'TRY_ENTERPRISE_STUDIO');
                    }}
                    data-cy="try-enterprise-studio-link"
                >
                    {translate({ id: 'TRY_ENTERPRISE_STUDIO' })}
                </Typography>
            </div>
        </UiBackgroundV2>
    );
};

export default AutomationCloudForCommunity;
